import React from 'react';
import { Col, isZeroOrDefined, Row } from '../../lib/utils';
import { CoachAverageStats } from '../../services/useFeedDashboardQueryService';
import FeedCenterStatisticsFeedWritingPercentIcon from './Icon/FeedCenterStatisticsFeedWritingPercentIcon';
import { BoldText, DemiLightText, LightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import RateChangeText from './RateChangeText';
import { TooltipWrapper } from '../../components/TooltipWrapper';
import SvgIcon from '../../components/SvgIcon';
import FeedCenterStatisticsReregister from './Icon/FeedCenterStatisticsReregister';
import FeedCenterStatisticsReregisterSuccessIcon from './Icon/FeedCenterStatisticsReregisterSuccessIcon';
import { RE_REGISTRATION_SUCCESS_NAMES } from './coachPerformanceModel';

type Props = {
    coachAverageStats: CoachAverageStats;
};

function FeedCoachStatisticsPageCoachAverageStats({ coachAverageStats }: Props) {
    const reportWriteRate = coachAverageStats?.reportWriteRate ?? undefined;
    const reportCount = coachAverageStats?.reportCount;
    const scheduleCount = coachAverageStats?.scheduleCount;
    const centerReportWriteRate = coachAverageStats?.centerReportWriteRate ?? undefined;
    const reportWriteRateChange = coachAverageStats?.reportWriteRateChange ?? undefined;
    const reportWriteRateRank = coachAverageStats?.reportWriteRateRank;
    const reportWriteRateRankChange = coachAverageStats?.reportWriteRateRankChange;
    const reRegistrationRate = coachAverageStats?.reRegistrationRate ?? undefined;
    const centerReRegistrationRate = coachAverageStats?.centerReRegistrationRate ?? undefined;
    const activeMemberCount = coachAverageStats?.activeMemberCount;
    const reRegisteredMemberCount = coachAverageStats?.reRegisteredMemberCount;
    const reRegistrationRateChange = coachAverageStats?.reRegistrationRateChange ?? undefined;
    const reRegistrationRateRank = coachAverageStats?.reRegistrationRateRank;
    const reRegistrationRateRankChange = coachAverageStats?.reRegistrationRateRankChange;
    const reRegistrationSuccessRate = coachAverageStats?.reRegistrationSuccessRate ?? undefined;
    const centerReRegistrationSuccessRate = coachAverageStats?.centerReRegistrationSuccessRate ?? undefined;
    const reRegistrationSucceededMemberCount = coachAverageStats?.reRegistrationSucceededMemberCount ?? 0;
    const reRegistrationEndedMemberCount = coachAverageStats?.reRegistrationEndedMemberCount ?? 0;
    const reRegistrationSuccessRateChange = coachAverageStats?.reRegistrationSuccessRateChange ?? undefined;
    const reRegistrationSuccessRateRank = coachAverageStats?.reRegistrationSuccessRateRank;
    const reRegistrationSuccessRateRankChange = coachAverageStats?.reRegistrationSuccessRateRankChange;
    return (
        <Row className="flex w-full flex-wrap gap-[25px] mb-[25px]">
            <StatisticCard
                icon={<FeedCenterStatisticsFeedWritingPercentIcon iconSize={26} width={52} height={52} mr={12} />}
                label="일지 작성률"
                value={reportWriteRate}
                centerValue={centerReportWriteRate}
                change={reportWriteRateChange}
                rank={reportWriteRateRank}
                rankChange={reportWriteRateRankChange}
                barColor="#5088FF"
                tooltip={
                    <Col className="flex w-full items-center justify-center">
                        <LightText fontSize={12} lineHeight={16} color="white" className="mb-[4px]">
                            일지 수 / 수업수
                        </LightText>
                        <Col className="flex w-full h-[1px] bg-[#989B9F] mb-[4px]" />
                        <DemiLightText fontSize={16} lineHeight={21} color="white">
                            {reportCount} / {scheduleCount}
                        </DemiLightText>
                    </Col>
                }
            />

            <StatisticCard
                icon={<FeedCenterStatisticsReregister iconSize={26} width={52} height={52} mr={12} />}
                label="재등록 회원 비중"
                value={reRegistrationRate}
                centerValue={centerReRegistrationRate}
                change={reRegistrationRateChange}
                rank={reRegistrationRateRank}
                rankChange={reRegistrationRateRankChange}
                barColor="#C170FF"
                tooltip={
                    <Col className="flex w-full items-center justify-center">
                        <LightText fontSize={12} lineHeight={16} color="white" className="mb-[4px]">
                            재등록 회원 / 활성 회원
                        </LightText>
                        <Col className="flex w-full h-[1px] bg-[#989B9F] mb-[4px]" />
                        <DemiLightText fontSize={16} lineHeight={21} color="white">
                            {reRegisteredMemberCount} / {activeMemberCount}
                        </DemiLightText>
                    </Col>
                }
            />

            <StatisticCard
                icon={<FeedCenterStatisticsReregisterSuccessIcon iconSize={26} width={52} height={52} mr={12} />}
                label={RE_REGISTRATION_SUCCESS_NAMES.label}
                value={reRegistrationSuccessRate}
                centerValue={centerReRegistrationSuccessRate}
                change={reRegistrationSuccessRateChange}
                rank={reRegistrationSuccessRateRank}
                rankChange={reRegistrationSuccessRateRankChange}
                /* 🔴 %만 보여주지 않는다 — 3명 중 1명과 300명 중 100명은 다른 말이다 */
                basisNote={
                    isZeroOrDefined(reRegistrationSuccessRate)
                        ? `${RE_REGISTRATION_SUCCESS_NAMES.denominator} ${reRegistrationEndedMemberCount}명 중 ${reRegistrationSucceededMemberCount}명`
                        : undefined
                }
                barColor="#00A793"
                tooltip={
                    <Col className="flex w-full items-center justify-center">
                        <LightText fontSize={12} lineHeight={16} color="white" className="mb-[4px]">
                            {RE_REGISTRATION_SUCCESS_NAMES.numerator} / {RE_REGISTRATION_SUCCESS_NAMES.denominator}
                        </LightText>
                        <Col className="flex w-full h-[1px] bg-[#989B9F] mb-[4px]" />
                        <DemiLightText fontSize={16} lineHeight={21} color="white">
                            {reRegistrationSucceededMemberCount} / {reRegistrationEndedMemberCount}
                        </DemiLightText>
                    </Col>
                }
            />
        </Row>
    );
}

export default FeedCoachStatisticsPageCoachAverageStats;
type StatisticCardProps = {
    icon: React.ReactNode;
    label: string;
    value?: number | null;
    centerValue?: number | null;
    unit?: string;
    tooltip?: React.ReactNode;
    change?: number | null;
    rank?: number;
    rankChange?: number | null;
    /** 값 아래에 늘 보이는 분모 — 「정상 종료 63명 중 30명」 */
    basisNote?: string;
    barColor: string;
    centerBarColor?: string;
};
const StatisticCard = ({
    icon,
    label,
    value,
    centerValue,
    unit = '%',
    tooltip,
    change,
    rank,
    rankChange,
    basisNote,
    barColor,
    centerBarColor = '#EBEDEF',
}: StatisticCardProps) => {
    return (
        <Col className="flex w-[261px] h-full bg-white border-[0.5px] border-solid border-[#E8E8E8] rounded-[19px] py-[29px] px-[27px] min-h-[280px]">
            <Row className="flex w-full h-full items-center mb-[12px]">
                {icon}
                <Row className="flex items-end">
                    <BoldText fontSize={32} lineHeight={37} color="#111">
                        {isZeroOrDefined(value) ? value : '-'}
                    </BoldText>
                    <BoldText fontSize={17} lineHeight={22} color="#111" className="ml-[6px] mr-[10px]">
                        {isZeroOrDefined(value) ? unit : ''}
                    </BoldText>
                    <RegularText fontSize={15} lineHeight={20} color="#6D7683">
                        {label}
                    </RegularText>
                </Row>
            </Row>

            {basisNote && (
                <DemiLightText fontSize={13} lineHeight={18} color="#9A9FA6" className="mb-[8px]">
                    {basisNote}
                </DemiLightText>
            )}

            <Col className="flex w-full h-full">
                <TooltipWrapper className="flex w-full" tooltip={{ customContent: tooltip, px: 12, py: 8 }}>
                    <Row className="flex w-full items-center mb-[8px]">
                        <Row className="flex items-center w-[60%]">
                            <Col
                                style={{ width: isZeroOrDefined(value) ? `${value}%` : '0%', backgroundColor: barColor }}
                                className="h-[13px] rounded-[50px] shrink-0 mr-[5px]"
                            />
                            <MediumText
                                fontSize={15}
                                lineHeight={20}
                                color={barColor}
                                className="shrink-0 whitespace-nowrap"
                            >
                                {isZeroOrDefined(value) ? `${value}${unit}` : '-'}
                            </MediumText>
                        </Row>
                    </Row>
                </TooltipWrapper>

                <Row className="flex w-full items-center mb-[44px]">
                    <Row className="flex relative items-center w-[60%]">
                        <Col
                            style={{
                                width: isZeroOrDefined(centerValue) ? `${centerValue}%` : '0%',
                                backgroundColor: centerBarColor,
                            }}
                            className="h-[13px] rounded-[50px] shrink-0 mr-[5px]"
                        />
                        <DemiLightText
                            fontSize={13}
                            lineHeight={18}
                            color="#9A9FA6"
                            className="shrink-0 whitespace-nowrap"
                        >
                            {isZeroOrDefined(centerValue) ? `센터 평균 ${centerValue}${unit}` : '센터 평균 -'}
                        </DemiLightText>
                    </Row>
                </Row>

                <Row className="flex w-full items-center mb-[15px] justify-between">
                    <Row className="flex items-center">
                        <SvgIcon name="SvgLastMonthGraph" size={16} fill="#fff" className="mr-[12px]" />
                        <DemiLightText fontSize={15} lineHeight={20} color="#626A76">
                            전월 대비
                        </DemiLightText>
                    </Row>
                    <RateChangeText value={change} />
                </Row>

                <Row className="flex w-full items-center justify-between">
                    <Row className="flex items-center">
                        <SvgIcon name="SvgCenterRanking" size={17} fill="#fff" className="mr-[12px]" />
                        <DemiLightText fontSize={15} lineHeight={20} color="#626A76">
                            센터 내 순위
                        </DemiLightText>
                    </Row>
                    <Row className="flex items-center">
                        <RegularText fontSize={16} lineHeight={21} color="#626A76" className="mr-[6px]">
                            {rank ? `${rank}등` : ''}
                        </RegularText>
                        <RateChangeText value={rankChange} isPercentage={false} />
                    </Row>
                </Row>
            </Col>
        </Col>
    );
};
